// src/pages/MyPage.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import axios from '../api/axios';
import { CheckCircle2, Loader2, Calendar, Clock, ChevronRight, AlertCircle, Video, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PaymentConfirmDialog from '../components/PaymentConfirmDialog';
import DiagnosisDetailDialog from '../components/DiagnosisDetailDialog';
import QuestionnaireDetailDialog from '../components/QuestionnaireDetailDialog';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useAuth } from '../context/AuthContext';
import usePresenceHeartbeat from '../hooks/usePresenceHeartbeat';
import { getUserProfile } from '../api/userProfile';
import UnifiedConfirmDialog from '../components/UnifiedConfirmDialog';
import WeChatGroupFloat from '../components/WeChatGroupFloat';
import CouponDialog from '../components/CouponDialog';
import { useMypageContext } from '../components/MypageShell';

import ProfileGuardDialog, {
  GuardDecision,
  isProfileGuardSnoozed,
  markSnooze1Day,
  markSnoozeSession,
  SESSION_SNOOZE_KEY,
} from '../components/ProfileGuardDialog';
import QuestionnaireGuardDialog, { QuestDecision } from '../components/QuestionnaireGuardDialog';


interface MsgThread {
  reservation_id: number;
  unread_count: number;
}

interface MyReservation {
  id: number;
  service: string;
  date: string;
  time: string;
  status: string;
  zoom_join_url?: string;
  zoom_meeting_id?: string | null;
  prescription_url?: string;
  total_price?: number;
  total_amount_jpy?: number;
  prescription_text?: string | null;
  diagnosis?: string | null;
  notes?: string | null;
  user_profile?: any;
  visit_type: 'first' | 'followup' | 'refill' | '';
  is_questionnaire_filled?: boolean;
  payment_status?: 'paid' | 'unpaid' | 'failed' | null;
  receipt_url?: string | null;
  transaction_id?: string | null;
  medicine_names?: string; // New field
  refill_items?: { name: string; unit_amount: number; quantity: number }[]; // Added

  patient_chart_number?: string | null;
  chart_number?: string | null;
  doctor_name?: string | null;
}

// ... existing code ...




interface UserProfile {
  full_name?: string;
  furigana?: string;
  gender?: string;
  birth_date?: string;
  phone_number?: string;
  postal_code?: string;
  address?: string;

  chart_number?: string | null;
}

type CouponBindPhase = 'hidden' | 'binding' | 'ok' | 'error';

const JOIN_EARLY_MINUTES = 30;
const JOIN_EXPIRES_MINUTES = 60;
const BTN_SIZE = '3.5rem';

const OPT_KEY = 'mypage_profile_nudge_optout';
const SNOOZE_KEY = 'mypage_profile_nudge_snooze_until_ms';

const digits = (s?: string | null) => String(s || '').replace(/\D/g, '');
const isYYYYMMDD = (s?: string | null) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { profileStatus, refreshProfileStatus } = useMypageContext();
  // マイページ滞在中も在席ハートビートを送る（予約直前に緑ランプを点灯させるため）
  usePresenceHeartbeat({ source: 'mypage' });

  type AuthMaybe = {
    user?: { chart_number?: string | null } | null;
    currentUser?: { chart_number?: string | null } | null;
    me?: { chart_number?: string | null } | null;
    chart_number?: string | null;
  };
  const auth = useAuth() as unknown as AuthMaybe;

  const pickChartNumber = (): string | null => {
    const fromAuth =
      auth?.user?.chart_number ??
      auth?.currentUser?.chart_number ??
      auth?.me?.chart_number ??
      auth?.chart_number ??
      null;
    if (fromAuth) return fromAuth;

    try {
      const keys = ['current_user', 'user', 'me'];
      for (const k of keys) {
        const raw = localStorage.getItem(k);
        if (raw) {
          const u = JSON.parse(raw);
          const cn =
            u?.chart_number ??
            u?.profile?.chart_number ??
            null;
          if (cn) return cn;
        }
      }
    } catch { }
    return null;
  };

  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toLowerCase();
  const isJa = lang.startsWith('ja');
  const isZh = lang.startsWith('zh');

  const [reservations, setReservations] = useState<MyReservation[]>([]);
  const [cancelingId, setCancelingId] = useState<number | null>(null);

  const [msgThreads, setMsgThreads] = useState<MsgThread[]>([]);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<MyReservation | null>(null);
  const [showDiagnosisDetail, setShowDiagnosisDetail] = useState<MyReservation | null>(null);
  const [showQuestionnaireDetail, setShowQuestionnaireDetail] = useState<MyReservation | null>(null);

  const [profile, setProfile] = useState<UserProfile | null>(null);

  const [showPwdDlg, setShowPwdDlg] = useState(false);
  const [showCouponDlg, setShowCouponDlg] = useState(false);

  const [payBanner, setPayBanner] = useState<{ rid?: number; phase: 'hidden' | 'success' | 'syncing' | 'done' }>({
    phase: 'hidden',
  });

  const [confirmConfig, setConfirmConfig] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'question' | 'danger' | 'warning';
  }>({ open: false, title: '', message: '', onConfirm: () => { } });

  const [nudgeOptOut, setNudgeOptOut] = useState<boolean>(() => localStorage.getItem(OPT_KEY) === '1');
  const [nudgeSnoozed, setNudgeSnoozed] = useState<boolean>(() => {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || '0');
    return Date.now() < until;
  });

  const [couponBindPhase, setCouponBindPhase] = useState<CouponBindPhase>('hidden');
  const [couponBindMsg, setCouponBindMsg] = useState<string>('');
  const [couponTrying, setCouponTrying] = useState<{ code?: string; link_code?: string } | null>(null);

  const [guardOpen, setGuardOpen] = useState(false);
  const [isConsultationTrigger, setIsConsultationTrigger] = useState(false);
  const guardResolverRef = useRef<((d: GuardDecision) => void) | null>(null);

  const [questGuardOpen, setQuestGuardOpen] = useState(false);
  const questGuardResolverRef = useRef<((d: QuestDecision) => void) | null>(null);

  // ===== 基本数据获取（不做兜底/二次校验）=====
  const fetchReservations = async () => {
    try {
      const res = await axios.get<MyReservation[]>('/reservations');
      setReservations(res.data || []);
    } catch (err: any) {
      console.error('fetch reservations failed:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  const fetchLatestJoinUrl = async (id: number): Promise<string | null> => {
    try {
      const res = await axios.get<MyReservation>(`/reservations/${id}`);
      return res.data?.zoom_join_url || null;
    } catch {
      return null;
    }
  };

  const fetchMsgThreads = useCallback(async () => {
    try {
      const res = await axios.get<MsgThread[]>('/doctor-messages');
      setMsgThreads(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchProfile = async () => {
    try {
      const p = await getUserProfile();
      setProfile(p || null);
    } catch {
      setProfile(null);
    }
  };

  const fmtJPY = (n?: number | null) => {
    if (n == null) return null;
    try {
      return new Intl.NumberFormat('ja-JP').format(n);
    } catch {
      return String(n);
    }
  };

  const handleCancel = async (id: number) => {
    setConfirmConfig({
      open: true,
      title: t('common.confirm') || '確認',
      message: t('mypage.cancel_confirm'),
      type: 'danger',
      onConfirm: async () => {
        setConfirmConfig(prev => ({ ...prev, open: false }));
        try {
          setCancelingId(id);
          await axios.delete(`/reservations/${id}`);
          alert(t('mypage.cancel_success'));
          fetchReservations();
        } catch {
          alert(t('mypage.cancel_failed'));
        } finally {
          setCancelingId(null);
        }
      }
    });
  };



  // 初回
  useEffect(() => {
    fetchReservations();
    fetchProfile();
    fetchMsgThreads();
  }, []);

  // state.refresh
  useEffect(() => {
    if ((location.state as any)?.refresh) {
      fetchReservations();
      fetchProfile();
      refreshProfileStatus();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // 支付回跳（原逻辑保持）
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pay = params.get('pay');
    const ridStr = params.get('rid');
    const sessionId = params.get('session_id') || '';
    const status = (params.get('status') || '').toLowerCase();
    const rid = ridStr ? Number(ridStr) : undefined;

    if (!pay) return;

    const gotoRecords = (q?: { status?: string; rid?: number | string }) => {
      const next = new URLSearchParams();
      next.set('tab', 'records');
      if (q?.status) next.set('status', q.status);
      if (q?.rid) next.set('rid', String(q.rid));
      navigate(`/mypage/records?${next.toString()}`, { replace: true });
    };

    if (pay === 'cancel') {
      gotoRecords({ status: status || undefined, rid });
      return;
    }

    if (pay === 'success' && rid) {
      let stopped = false;
      setPayBanner({ rid, phase: 'success' });

      (async () => {
        try {
          setPayBanner({ rid, phase: 'syncing' });
          if (sessionId) {
            const resp = await axios.post('/pay/confirm', { session_id: sessionId });
            if (resp.data?.payment_status === 'paid') {
              await fetchReservations();
              if (!stopped) {
                setPayBanner({ rid, phase: 'done' });
                setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
              }
              gotoRecords({ status: 'completed', rid });
              return;
            }
          }
        } catch { }

        const start = Date.now();
        const poll = async () => {
          if (stopped) return;
          try {
            const s = await axios.get('/pay/status', { params: { reservation_id: rid } });
            if (s.data?.payment_status === 'paid') {
              await fetchReservations();
              setPayBanner({ rid, phase: 'done' });
              setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
              gotoRecords({ status: 'completed', rid });
              return;
            }
          } catch { }
          if (Date.now() - start > 15000) {
            await fetchReservations();
            setPayBanner({ rid, phase: 'done' });
            setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
            gotoRecords({ status: 'completed', rid });
            return;
          }
          setTimeout(poll, 1500);
        };
        poll();
      })();

      return () => {
        stopped = true;
      };
    }
  }, [location.search, navigate]);

  // 券码绑定（原逻辑）
  const readPendingCoupon = (): { code?: string; link_code?: string } | null => {
    const code = sessionStorage.getItem('pending_coupon_code') || undefined;
    const link_code = sessionStorage.getItem('pending_coupon_link_code') || undefined;
    if (!code && !link_code) return null;
    return { code, link_code };
  };
  const clearPendingCoupon = () => {
    sessionStorage.removeItem('pending_coupon_code');
    sessionStorage.removeItem('pending_coupon_link_code');
  };
  const bindPendingCoupon = async (initial?: { code?: string; link_code?: string }) => {
    const payload = initial ?? readPendingCoupon();
    if (!payload) return;

    setCouponTrying(payload);
    setCouponBindPhase('binding');
    setCouponBindMsg('');

    try {
      await axios.post('/user/coupons/bind', payload);
      clearPendingCoupon();
      setCouponBindPhase('ok');
      setCouponBindMsg(
        t('mypage.coupon_bound_success') ||
        'クーポンをアカウントに適用しました。マイページの「クーポン」で確認できます。'
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        t('mypage.coupon_bind_failed') ||
        'クーポンの適用に失敗しました。時間をおいて再試行してください。';
      setCouponBindPhase('error');
      setCouponBindMsg(String(msg));
    }
  };
  useEffect(() => {
    const pending = readPendingCoupon();
    if (pending) bindPendingCoupon(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 聚焦/可见刷新
  useEffect(() => {
    const onFocus = () => fetchReservations();
    const onVisible = () => { if (!document.hidden) fetchReservations(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // storage 信号刷新（问诊提交页可 setItem('questionnaire_submitted','1')）
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'questionnaire_submitted' && e.newValue === '1') {
        fetchReservations().finally(() => {
          try { localStorage.removeItem('questionnaire_submitted'); } catch { }
        });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const openProfileGuard = async (isConsultation: boolean = false): Promise<GuardDecision | 'skip'> => {
    if (profileStatus.state === 'green') return 'skip';
    if (isProfileGuardSnoozed()) return 'skip';
    if (nudgeOptOut || nudgeSnoozed) return 'skip';
    if (profileStatus.state === 'yellow') return 'skip';

    return await new Promise<GuardDecision>((resolve) => {
      setIsConsultationTrigger(isConsultation);
      guardResolverRef.current = resolve;
      setGuardOpen(true);
    });
  };

  const onGuardClose = (decision: GuardDecision) => {
    setGuardOpen(false);
    const resolver = guardResolverRef.current;
    guardResolverRef.current = null;
    if (resolver) resolver(decision);
  };

  const ensureProfileGuard = async (isConsultation: boolean = false): Promise<boolean> => {
    const decision = await openProfileGuard(isConsultation);
    if (decision === 'skip') return true;

    if (decision === 'goProfile') {
      navigate('/mypage/profile');
      return false;
    }
    if (decision === 'snoozeSession') {
      markSnoozeSession();
      return true;
    }
    if (decision === 'snooze1d') {
      markSnooze1Day();
      return true;
    }
    if (decision === 'continue') {
      return true;
    }
    return false;
  };

  // ===== 问诊票守卫逻辑 =====
  const openQuestGuard = async (): Promise<QuestDecision | 'skip'> => {
    // 这里仅在入室流程中显式调用，所以逻辑相对简单
    return await new Promise<QuestDecision>((resolve) => {
      questGuardResolverRef.current = resolve;
      setQuestGuardOpen(true);
    });
  };

  const onQuestGuardClose = (decision: QuestDecision) => {
    setQuestGuardOpen(false);
    const resolver = questGuardResolverRef.current;
    questGuardResolverRef.current = null;
    if (resolver) resolver(decision);
  };

  const ensureQuestionnaireGuard = async (r: MyReservation): Promise<boolean> => {
    // 如果已经填写，直接跳过
    if (r.is_questionnaire_filled) return true;

    const decision = await openQuestGuard();
    if (decision === 'skip') return true;

    if (decision === 'goFill') {
      const path = `/questionnaire/${r.service.replace(/_/g, '-')}`;
      navigate(path, {
        state: {
          reservationId: r.id,
          fromMyPage: true,
          visitType: r.visit_type || 'first',
          service: r.service,
        },
      });
      return false;
    }
    if (decision === 'continue') {
      return true;
    }
    return false;
  };

  const btnLongBase = "w-full py-3 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-sm text-[14px] active:scale-[0.98]";
  const btnOrange = "bg-orange-500 text-white hover:bg-orange-600 shadow-orange-100";
  const btnOutline = "border border-slate-200 text-slate-600 bg-white hover:bg-slate-50";
  const btnDisabled = "bg-orange-500/50 text-white cursor-not-allowed shadow-none";
  const btnEmerald = "border-2 border-orange-400 text-orange-500 bg-white hover:bg-orange-50 shadow-sm";
  const btnIndigo = "border-2 border-orange-400 text-orange-500 bg-white hover:bg-orange-50 shadow-sm";
  const btnConsultation = "bg-orange-500 text-white hover:bg-orange-600 shadow-[0_0_16px_rgba(249,115,22,0.5)] hover:shadow-[0_0_22px_rgba(249,115,22,0.7)] transition-all duration-300";

  const closeQuestionnaireDetail = () => {
    setShowQuestionnaireDetail(null);
    fetchReservations();
  };

  return (
    <>
      {payBanner.phase !== 'hidden' && (
        <div className="fixed top-16 left-0 right-0 z-40">
          <div className="mx-auto max-w-3xl px-6">
            <div
              className={classNames(
                'rounded-xl border px-4 py-3 shadow-sm',
                payBanner.phase === 'done' ? 'bg-emerald-50 border-emerald-200' : 'bg-coral/5 border-coral/20'
              )}
            >
              <div className="flex items-center gap-2 text-sm">
                {payBanner.phase === 'syncing' ? (
                  <Loader2 className="w-4 h-4 animate-spin text-coral" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                )}
                <span className="text-gray-800">
                  {payBanner.phase === 'success' && t('payment.returned_success')}
                  {payBanner.phase === 'syncing' && t('payment.syncing')}
                  {payBanner.phase === 'done' && t('payment.synced_done')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 医師メッセージバナー */}
      {msgThreads.reduce((s, th) => s + th.unread_count, 0) > 0 && (
        <button
          onClick={() => navigate('/mypage/messages')}
          className="flex items-center justify-between w-full px-4 sm:px-6 py-2.5 bg-orange-50 border-b border-orange-100 hover:bg-orange-100 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={14} className="text-orange-500 shrink-0" />
            <span className="text-sm font-medium text-orange-800">
              {t('mypage.doctor_msg_banner', { count: msgThreads.reduce((s, th) => s + th.unread_count, 0) })}
            </span>
          </div>
          <ChevronRight size={14} className="text-orange-400 shrink-0" />
        </button>
      )}

      <div className="flex flex-col flex-1">
        <div className="space-y-6 flex-1 flex flex-col">
            {/* Pending 区 */}
            {(() => {
              const list = reservations.filter((r) => r.status === 'pending');
              if (list.length === 0) return null;
              return (
                <>
                  <div className="flex gap-6 border-b mb-4">
                    <div className="px-1.5 py-2 text-sm md:text-base font-bold border-b-2 text-coral border-coral">
                      {t('mypage.tab_pending') || '予約確認中'}
                    </div>
                  </div>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    {list.map((r, idx) => {
                      const isPending = r.status === 'pending';
                      const isAssigned = r.status === 'assigned';
                      const questionnaireFilled = !!r.is_questionnaire_filled;
                      const needsQuestionnaire =
                        (isPending || isAssigned || r.status === 'reserved' || r.status === 'expired') && !questionnaireFilled;

                      return (
                        <motion.div
                          key={r.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col sm:flex-row justify-between gap-5 sm:gap-6"
                        >
                          <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400" />
                          <div className="flex-1">
                            <p className="text-lg font-bold text-gray-800">
                              {t(`service.${r.service}`, r.service)}{' '}
                              {r.visit_type && (
                                <span className="text-sm text-gray-600">（{t(`visit_type.${r.visit_type}`)}）</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {r.date} {r.time}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-100 text-[11px] font-bold">
                                {t('mypage.tab_pending') || '調整中（予約プール）'}
                              </span>
                            </div>

                            {profileStatus.state !== 'green' && (
                              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                * {t('mypage.profile_warn_cancel')}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col gap-3 w-full sm:w-48 self-center sm:self-start">
                            <div className="space-y-2 w-full">
                              <div className="flex flex-col w-full relative">
                                <button
                                  onClick={async () => {
                                    const ok = await ensureProfileGuard();
                                    if (!ok) return;

                                    if (needsQuestionnaire) {
                                      const path = `/questionnaire/${r.service.replace(/_/g, '-')}`;
                                      navigate(path, {
                                        state: {
                                          reservationId: r.id,
                                          fromMyPage: true,
                                          visitType: r.visit_type || 'first',
                                          service: r.service,
                                        },
                                      });
                                    } else {
                                      setShowQuestionnaireDetail(r);
                                    }
                                  }}
                                  className={classNames(btnLongBase, needsQuestionnaire ? btnOrange : btnEmerald)}
                                >
                                  {needsQuestionnaire ? t('mypage.fill_questionnaire') : t('mypage.view_questionnaire')}
                                </button>
                                
                                {needsQuestionnaire && (
                                  <p className="text-[10px] text-red-500 font-bold mt-1 text-right pr-2">
                                     * {t('mypage.need_input_badge', '事前入力が必要です！')}
                                  </p>
                                )}
                              </div>

                              <button
                                disabled
                                className={classNames(btnLongBase, btnDisabled)}
                                title={t('mypage.start_consultation') || '診療開始'}
                              >
                                {t('mypage.start_consultation')}
                              </button>
                            </div>

                            <button
                              onClick={() => handleCancel(r.id)}
                              className="text-[13px] text-slate-400 underline decoration-slate-300 underline-offset-4 hover:text-red-500 transition-colors text-center w-full mt-2"
                            >
                              {t('mypage.cancel_reservation')}
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </>
              );
            })()}

            {/* reserved / assigned 等待区 */}
            {(() => {
              const waitingList = reservations.filter((r) => r.status === 'reserved' || r.status === 'assigned' || r.status === 'expired');
              if (waitingList.length === 0) return null;

              return (
                <>
                  <div className="flex gap-6 border-b mb-4">
                    <div className="py-2 text-sm font-semibold border-b-2 text-coral border-coral">
                      {t('mypage.tab_waiting')}
                    </div>
                  </div>

                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    {waitingList.map((r, idx) => {
                      const reservationDateTime = dayjs(`${r.date} ${r.time}`, 'YYYY-MM-DD HH:mm');
                      const now = dayjs();
                      const isPending = r.status === 'pending';
                      const isAssigned = r.status === 'assigned';

                      // 担当医が確定済み（＝Zoom有効）の予約は、開始からの経過時間で入室ボタンを
                      // 永久ロックしない（掉线再接続を締め出さないため）。実際の終了判定は医師側の
                      // 「患者未着で終了」ボタン(T2)に一本化する（T6：入室/再入室健壮性審計）。
                      const afterStart = now.isAfter(reservationDateTime.subtract(JOIN_EARLY_MINUTES, 'minute'));
                      const withinNormalWindow = afterStart && now.isBefore(reservationDateTime.add(JOIN_EXPIRES_MINUTES, 'minute'));
                      const canStart = isAssigned ? afterStart : withinNormalWindow;
                      const expiredJoinWindow = !isAssigned && now.isAfter(reservationDateTime.add(JOIN_EXPIRES_MINUTES, 'minute'));
                      const questionnaireFilled = !!r.is_questionnaire_filled;
                      const needsQuestionnaire =
                        (isPending || isAssigned || r.status === 'reserved' || r.status === 'expired') &&
                        !questionnaireFilled;

                      const statusPillText =
                        r.status === 'expired' ? t('status.expired') || '予約期限切れ' :
                        r.status === 'assigned' ? t('mypage.assigned_doctor') || '担当医確定' : t('mypage.reserved') || '日時確定';

                      const windowPillText = expiredJoinWindow
                        ? t('mypage.expired_join_window', { minutes: JOIN_EXPIRES_MINUTES })
                        : canStart
                          ? t('mypage.can_join_now')
                          : t('mypage.cannot_join_yet');

                      return (
                        <motion.div
                          key={r.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={classNames(
                            "relative overflow-hidden rounded-2xl border shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row justify-between sm:items-start gap-5 sm:gap-6 transition-shadow hover:shadow-md",
                            canStart ? "bg-orange-50/60 border-orange-200 ring-2 ring-orange-300/40" : "bg-white border-slate-200"
                          )}
                        >
                          {canStart && (
                            <div className="absolute top-0 right-0 px-4 py-1 bg-orange-500 text-white text-[10px] font-bold rounded-bl-2xl animate-pulse">
                              {t('mypage.live_now', 'LIVE')}
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-lg font-bold text-gray-800">
                              {t(`service.${r.service}`, r.service)}{' '}
                              {r.visit_type && (
                                <span className="text-sm text-gray-600">（{t(`visit_type.${r.visit_type}`)}）</span>
                              )}
                            </p>

                            <p className="text-sm text-gray-600 mt-1">
                              {r.date} {r.time}
                            </p>

                            <div className="mt-2 flex flex-wrap gap-2">
                                <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-orange-50 text-orange-600 border border-orange-100 whitespace-nowrap">
                                  {statusPillText}
                                </span>
                                <span
                                  className={classNames(
                                    'text-[11px] font-bold px-3 py-1 rounded-full border whitespace-nowrap',
                                    expiredJoinWindow
                                      ? 'bg-red-50 text-red-700 border-red-100'
                                      : canStart
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                        : 'bg-slate-50 text-slate-500 border-slate-200'
                                  )}
                                >
                                  {windowPillText}
                                </span>
                                {r.zoom_meeting_id && (
                                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
                                    ID: {r.zoom_meeting_id}
                                  </span>
                                )}
                            </div>


                            {profileStatus.state !== 'green' && (
                              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                * {t('mypage.profile_warn_cancel')}
                              </p>
                            )}

                            <p className="text-xs text-slate-400 mt-2">{t('mypage.consultation_notice')}</p>
                            {/iPhone|Android|iPad/i.test(navigator.userAgent) && (
                              <p className="text-xs text-red-400 mt-1 font-bold">{t('mypage.zoom_notice') || '※ Zoom アプリのインストールが必要です。'}</p>
                            )}
                          </div>

                          <div className="flex flex-col gap-3 w-full sm:w-48 self-center sm:self-start">
                            <div className="space-y-2 w-full">
                              <button
                                onClick={async () => {
                                  const ok = await ensureProfileGuard();
                                  if (!ok) return;
                                  if (needsQuestionnaire) {
                                    const path = `/questionnaire/${r.service.replace(/_/g, '-')}`;
                                    navigate(path, {
                                      state: {
                                        reservationId: r.id,
                                        fromMyPage: true,
                                        visitType: r.visit_type || 'first',
                                        service: r.service,
                                      },
                                    });
                                  } else {
                                    setShowQuestionnaireDetail(r);
                                  }
                                }}
                                className={classNames(btnLongBase, needsQuestionnaire ? btnOrange : btnEmerald)}
                              >
                                {needsQuestionnaire ? t('mypage.fill_questionnaire') : t('mypage.view_questionnaire')}
                              </button>

                              {needsQuestionnaire && (
                                <p className="text-[10px] text-red-500 font-bold mt-1 text-center font-sans">
                                   {t('mypage.need_input_badge', '事前入力が必要です！')}
                                </p>
                              )}

                              <button
                                onClick={async () => {
                                  // 第一関：基本情報確認
                                  const okProfile = await ensureProfileGuard(true);
                                  if (!okProfile) return;

                                  // 第二関：問診票確認
                                  const okQuest = await ensureQuestionnaireGuard(r);
                                  if (!okQuest) return;

                                  navigate(`/consultation/${r.id}`);
                                }}
                                disabled={!canStart}
                                className={classNames(btnLongBase, canStart ? btnConsultation : btnDisabled, canStart && "animate-consultation-active")}
                                title={!canStart ? (t('mypage.start_unavailable_now') || '') : ''}
                              >
                                <Video className={classNames("w-4 h-4", canStart ? "animate-pulse" : "")} />
                                {t('mypage.start_consultation')}
                              </button>
                            </div>

                            <button
                              onClick={() => handleCancel(r.id)}
                              disabled={cancelingId === r.id || (r.status !== 'expired' && canStart)}
                              className={classNames(
                                "text-[13px] underline decoration-slate-300 underline-offset-4 transition-colors text-center w-full mt-2",
                                (cancelingId === r.id || (r.status !== 'expired' && canStart))
                                  ? "text-slate-200 cursor-not-allowed decoration-transparent"
                                  : "text-slate-400 hover:text-red-500"
                              )}
                            >
                              {cancelingId === r.id ? t('mypage.canceling') : t('mypage.cancel_reservation')}
                            </button>

                            {canStart && (
                            <p className="mt-1 text-[10px] text-orange-500 font-bold text-center animate-pulse">
                                {t('mypage.doctor_joins_within_15m')}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </>
              );
            })()}

            {/* 未支付完成区（包含：1) 記録已齐 -> 可支付；2) 記録未齐 -> 提示文本） */}
            {(() => {
              const recordComplete = (r: MyReservation) => !!(r.diagnosis && r.prescription_text && r.notes);

              // 1) Consultations (completed, full record, unpaid)
              const readyConsultations = reservations.filter(
                (r) => r.visit_type !== 'refill' && r.status === 'completed' && recordComplete(r) && (r.payment_status || 'unpaid') !== 'paid'
              );

              // 2) Refills (completed, full record, unpaid)
              const readyRefills = reservations.filter(
                (r) => r.visit_type === 'refill' && r.status === 'completed' && recordComplete(r) && (r.payment_status || 'unpaid') !== 'paid'
              );

              // 3) Completed but missing records (Consultation only? Or both?)
              // Generally Refills should have records copied.
              const pendingRecordList = reservations.filter(
                (r) => r.status === 'completed' && !recordComplete(r)
              );

              if (readyConsultations.length === 0 && readyRefills.length === 0 && pendingRecordList.length === 0) return null;

              return (
                <div className="space-y-10">
                  {/* --- Section A: Consultation Unpaid --- */}
                  {(readyConsultations.length > 0 || pendingRecordList.length > 0) && (
                    <div>
                      <div className="flex gap-6 border-b mb-4">
                        <div className="py-2 text-sm font-semibold border-b-2 text-coral border-coral">
                          {t('mypage.tab_unpaid')}
                        </div>
                      </div>

                      {/* Pending Records (Text Only) */}
                      {pendingRecordList.length > 0 && (
                        <div className="space-y-4 mb-6">
                          {pendingRecordList.map((r) => (
                            <div
                              key={`pending-record-${r.id}`}
                              className="border-x-0 sm:border-x border-y sm:border-slate-200 rounded-none sm:rounded-xl shadow-none sm:shadow-sm p-5 bg-gray-50 flex flex-col gap-2"
                            >
                              <p className="text-lg font-bold text-gray-800 mb-1">
                                {t(`service.${r.service}`, r.service)}{' '}
                                {r.visit_type && (
                                  <span className="text-sm text-gray-600">（{t(`visit_type.${r.visit_type}`)}）</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600">
                                {r.date} {r.time}
                              </p>
                              <p className="text-sm text-gray-500">{t('mypage.completed')}</p>
                              <p className="text-sm text-amber-700">
                                {t('mypage.medical_record_pending') ||
                                  '診療記録を確認中です。記録が確定すると内容の閲覧とお支払いができるようになります。'}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Ready Consultations */}
                      {readyConsultations.length > 0 && (
                        <div className="space-y-4">
                          {readyConsultations.map((r) => (
                            <div
                              key={`ready-${r.id}`}
                              className="relative overflow-hidden border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6 bg-white flex flex-col sm:flex-row justify-between gap-5 sm:gap-6"
                            >
                              <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400" />
                              <div className="flex-1">
                                <p className="text-lg font-bold text-gray-800 mb-1">
                                  {t(`service.${r.service}`, r.service)}{' '}
                                  {r.visit_type && (
                                    <span className="text-sm text-gray-600">（{t(`visit_type.${r.visit_type}`)}）</span>
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {r.date} {r.time}
                                </p>
                                <div className="mt-2 mb-3">
                                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 whitespace-nowrap">
                                    {t('mypage.completed')}
                                  </span>
                                </div>

                                {profileStatus.state !== 'green' && (
                                  <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                    * {t('mypage.profile_warn_cancel')}
                                  </p>
                                )}
                              </div>

                              <div className="flex flex-col gap-3 w-full sm:w-48 self-center sm:self-start">
                                {/* 支払い予定金額 */}
                                {typeof r.total_amount_jpy === 'number' && r.total_amount_jpy > 0 && (
                                  <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-center">
                                    <p className="text-[11px] text-gray-500 mb-0.5">{t('mypage.payment_due_label') || 'お支払い予定金額'}</p>
                                    <p className="text-lg font-bold text-orange-500">¥{r.total_amount_jpy.toLocaleString()}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">{t('mypage.coupon_apply_at_payment') || '※クーポンは支払い時に適用できます'}</p>
                                  </div>
                                )}
                                <div className="space-y-2 w-full">
                                  <button
                                    onClick={async () => {
                                      const ok = await ensureProfileGuard();
                                      if (!ok) return;
                                      setSelectedReservation(r);
                                      setShowPayment(true);
                                    }}
                                    className={classNames(btnLongBase, btnOrange)}
                                  >
                                    {t('mypage.pay_now')}
                                  </button>

                                  <button
                                    onClick={() => {
                                      const cn =
                                        pickChartNumber() ??
                                        (r as any)?.chart_number ??
                                        (r as any)?.patient_chart_number ??
                                        (r.user_profile as any)?.chart_number ??
                                        (profile as any)?.chart_number ??
                                        null;

                                      setShowDiagnosisDetail({
                                        ...r,
                                        chart_number: cn ?? undefined,
                                        patient_chart_number: (r as any)?.patient_chart_number ?? cn ?? undefined,
                                        user_profile: {
                                          ...(r.user_profile || {}),
                                          chart_number: (r.user_profile as any)?.chart_number ?? cn ?? undefined,
                                        },
                                      } as any);
                                    }}
                                    className={classNames(btnLongBase, btnIndigo)}
                                  >
                                    {t('mypage.view_diagnosis')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* --- Section B: Refill Unpaid --- */}
                  {readyRefills.length > 0 && (
                    <div>
                      <div className="flex gap-6 border-b mb-4">
                        <div className="py-2 text-sm font-semibold border-b-2 text-coral border-coral">
                          {t('mypage.extra_title') || '追加配送申請'}
                        </div>
                      </div>
                      <div className="space-y-4">
                        {readyRefills.map((r) => (
                          <div
                            key={`refill-${r.id}`}
                            className="relative overflow-hidden border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6 bg-white flex flex-col sm:flex-row justify-between gap-5 sm:gap-6"
                          >
                            <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400" />
                            <div className="flex-1">
                              <p className="text-lg font-bold text-gray-800 mb-2">
                                {t('mypage.extra_title') || '追加配送'}
                              </p>

                              {/* Details or Fallback */}
                              {r.refill_items && r.refill_items.length > 0 ? (
                                <div className="mb-3 space-y-1">
                                  {r.refill_items.map((it, idx) => (
                                    <div key={idx} className="text-sm text-gray-700 flex justify-between max-w-md">
                                      <span>
                                        {it.name}
                                        {it.quantity > 1 ? ` x ${it.quantity}` : ''}
                                      </span>
                                      <span>{fmtJPY(it.unit_amount * it.quantity)} 円</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-lg text-gray-800 mb-1">
                                  {r.medicine_names || t('visit_type.refill')}
                                </p>
                              )}

                              <p className="text-xs text-gray-500 mb-2">
                                {r.date} {r.time} (ID: {r.id})
                              </p>

                              <p className="text-sm font-semibold text-gray-900 border-t border-gray-200 pt-2 inline-block">
                                {t('mypage.amount')}: <span className="text-lg">{fmtJPY(r.total_amount_jpy)}</span> 円
                              </p>
                            </div>

                            <div className="flex flex-col gap-3 w-full sm:w-48 self-center sm:self-start">
                              <div className="space-y-2 w-full">
                                <button
                                  onClick={() => {
                                    setSelectedReservation(r);
                                    setShowPayment(true);
                                  }}
                                  className={classNames(btnLongBase, btnOrange)}
                                >
                                  {t('mypage.pay_now')}
                                </button>
                              </div>

                              <button
                                onClick={() => handleCancel(r.id)}
                                className="text-[13px] text-slate-400 underline hover:text-red-500 transition-colors text-center w-full mt-1"
                              >
                                {t('common.cancel') || 'キャンセル'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 空状态 */}
            {reservations.filter((r) => ['pending', 'reserved', 'assigned', 'expired'].includes(r.status)).length === 0 &&
              // 未支付区块也为空时才展示空状态：既无记录未齐完成，也无记录齐全未支付
              (reservations.filter((r) => r.status === 'completed' && !(r.diagnosis && r.prescription_text && r.notes)).length === 0) &&
              (reservations.filter((r) => r.status === 'completed' && (r.diagnosis && r.prescription_text && r.notes) && (r.payment_status || 'unpaid') !== 'paid').length === 0) && (
                <div className="text-center flex-1 w-full flex flex-col justify-center px-4">
                  <div className="flex justify-center mb-6">
                    {/* 性別が女性なら女の子画像、それ以外(男性/その他/未入力)は既定の男の子画像。
                        gender の格納値が 'female'(英語)/'女性'/'女'(日本語)と混在しているため両対応。
                        profile 読み込み完了までは画像を出さず、男の子→女の子のちらつきを防ぐ。 */}
                    {profile && (
                      <img
                        src={`/images/${['female', 'f', '女性', '女'].includes(String(profile?.gender || '').trim().toLowerCase()) ? 'mypagegirl' : 'mypagetop'}.png`}
                        alt="No Reservation"
                        className="w-full max-w-sm h-auto rounded-2xl"
                      />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('mypage.no_reservation')}</h2>
                  <p className="text-base text-gray-600 mb-4">{t('mypage.please_reserve')}</p>
                  <div className="space-y-3 w-full max-w-[280px] sm:max-w-xs mx-auto">
                    <button
                      onClick={() => navigate('/reserve')}
                      className={classNames(btnLongBase, btnOrange, "max-w-[280px] mx-auto")}
                    >
                      {t('mypage.reserve_now') || '診療予約'}
                    </button>
                  </div>
                </div>
              )}
            </div>
        </div>

        <WeChatGroupFloat />

      {selectedReservation && showPayment && (
        <PaymentConfirmDialog
          open={showPayment}
          onClose={() => {
            setShowPayment(false);
            setSelectedReservation(null);
          }}
          onConfirm={() => {
            // PaymentConfirmDialog が Stripe URL へリダイレクト済みのため、ここでは何もしない
            setShowPayment(false);
          }}
          reservation={selectedReservation as any}
        />
      )}

      {showDiagnosisDetail && (
        <DiagnosisDetailDialog reservation={showDiagnosisDetail as any} onClose={() => setShowDiagnosisDetail(null)} />
      )}

      {showQuestionnaireDetail && showQuestionnaireDetail.service && (
        <QuestionnaireDetailDialog
          key={`${showQuestionnaireDetail.id}-${showQuestionnaireDetail.service}-${(showQuestionnaireDetail.visit_type || '')}`}
          open={true}
          onClose={closeQuestionnaireDetail}
          reservationId={showQuestionnaireDetail.id}
          service={showQuestionnaireDetail.service}
          visitType={(showQuestionnaireDetail.visit_type as 'first' | 'followup' | '') || ''}
        />
      )}

      <CouponDialog open={showCouponDlg} onClose={() => setShowCouponDlg(false)} />

      <ChangePasswordDialog
        open={showPwdDlg}
        onClose={() => setShowPwdDlg(false)}
      />

      <UnifiedConfirmDialog
        open={confirmConfig.open}
        onClose={() => setConfirmConfig(prev => ({ ...prev, open: false }))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type={confirmConfig.type as any}
      />

      <ProfileGuardDialog open={guardOpen} onClose={onGuardClose} requireRiskCheck={isConsultationTrigger} missingFields={profileStatus.missing} />
      <QuestionnaireGuardDialog open={questGuardOpen} onClose={onQuestGuardClose} />
    </>
  );
};

export default MyPage;
